import React, { useEffect, useState } from 'react';
import {
  PlusOutlined,
  DeleteOutlined,
  SettingOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { servicesService } from '../../services';
import { useNotificationContext } from '../../contexts/NotificationContext';

/**
 * Componente global de reglas de seguridad (firewall ingress/egress) para
 * servicios que corren en EC2 dedicada con su propio security group
 * (VMs y todo el marketplace). Consume los endpoints genéricos ya existentes:
 *   POST  /services/{id}/compute/sg-rules  -> reconcilia contra AWS
 *   PATCH /services/{id}/compute/sg-rules  -> persiste la lista completa
 *
 * El backend (platform_ec2_admin) hace match por path.endswith('/sg-rules')
 * sin importar la categoría; solo requiere que el servicio tenga securityGroupId.
 */

interface SecurityRule {
  id: string;
  direction: 'ingress' | 'egress';
  type: 'RDP' | 'MSSQL' | 'MySQL' | 'PostgreSQL' | 'SSH' | 'HTTP' | 'HTTPS' | 'Custom';
  fromPort: number;
  toPort: number;
  protocol: 'tcp' | 'udp' | 'icmp';
  cidr: string;
  description: string;
}

interface SecurityRulesTabProps {
  serviceId: string;
  /** Objeto securityRules crudo del API del servicio: { ingress: [], egress: [] } */
  securityRules?: any;
  /** Si el servicio aún no tiene security group (p. ej. no ha terminado de provisionar) */
  securityGroupId?: string | null;
}

const EMPTY_RULE: Omit<SecurityRule, 'id'> = {
  direction: 'ingress',
  type: 'SSH',
  fromPort: 22,
  toPort: 22,
  protocol: 'tcp',
  cidr: '',
  description: '',
};

const getTypeFromPort = (fromPort: number, toPort: number, protocol: string): SecurityRule['type'] => {
  if (fromPort === 22 && toPort === 22 && protocol === 'tcp') return 'SSH';
  if (fromPort === 3389 && toPort === 3389 && protocol === 'tcp') return 'RDP';
  if (fromPort === 1433 && toPort === 1433 && protocol === 'tcp') return 'MSSQL';
  if (fromPort === 3306 && toPort === 3306 && protocol === 'tcp') return 'MySQL';
  if (fromPort === 5432 && toPort === 5432 && protocol === 'tcp') return 'PostgreSQL';
  if (fromPort === 80 && toPort === 80 && protocol === 'tcp') return 'HTTP';
  if (fromPort === 443 && toPort === 443 && protocol === 'tcp') return 'HTTPS';
  return 'Custom';
};

const mapSecurityRulesFromAPI = (
  securityRules: any,
  direction: 'ingress' | 'egress'
): SecurityRule[] => {
  if (!securityRules || !securityRules[direction] || !Array.isArray(securityRules[direction])) {
    return [];
  }
  // Solo mostramos reglas basadas en CIDR (control de acceso del cliente).
  // Las reglas "source security group" (sin cidr) son plumbing interno de Kubiy
  // — p.ej. la del ALB que sirve el sitio — y NO deben mostrarse ni editarse:
  // si el cliente las borra, rompe el acceso al servicio.
  return securityRules[direction]
    .filter((rule: any) => rule && rule.cidr && !rule.sourceSgId)
    .map((rule: any, index: number) => {
    const fromPort = Math.round(rule.fromPort || 0);
    const toPort = Math.round(rule.toPort || 0);
    const protocol = (rule.protocol || 'tcp').toLowerCase() as 'tcp' | 'udp' | 'icmp';
    return {
      id: `rule-${direction}-${index}-${Date.now()}`,
      direction,
      type: getTypeFromPort(fromPort, toPort, protocol),
      fromPort,
      toPort,
      protocol,
      cidr: rule.cidr || '',
      description: rule.description || '',
    };
  });
};

const getTypeConfig = (
  type: SecurityRule['type']
): { fromPort: number; toPort: number; protocol: 'tcp' | 'udp' } => {
  const configs: Record<SecurityRule['type'], { fromPort: number; toPort: number; protocol: 'tcp' | 'udp' }> = {
    RDP: { fromPort: 3389, toPort: 3389, protocol: 'tcp' },
    MSSQL: { fromPort: 1433, toPort: 1433, protocol: 'tcp' },
    MySQL: { fromPort: 3306, toPort: 3306, protocol: 'tcp' },
    PostgreSQL: { fromPort: 5432, toPort: 5432, protocol: 'tcp' },
    SSH: { fromPort: 22, toPort: 22, protocol: 'tcp' },
    HTTP: { fromPort: 80, toPort: 80, protocol: 'tcp' },
    HTTPS: { fromPort: 443, toPort: 443, protocol: 'tcp' },
    Custom: { fromPort: 0, toPort: 0, protocol: 'tcp' },
  };
  return configs[type];
};

const areRulesEqual = (
  r1: { protocol: string; fromPort: number; toPort: number; cidr: string },
  r2: { protocol: string; fromPort: number; toPort: number; cidr: string }
): boolean =>
  r1.protocol === r2.protocol && r1.fromPort === r2.fromPort && r1.toPort === r2.toPort && r1.cidr === r2.cidr;

const SecurityRulesTab: React.FC<SecurityRulesTabProps> = ({ serviceId, securityRules, securityGroupId }) => {
  const { success, error: showError } = useNotificationContext();

  const [ingressRules, setIngressRules] = useState<SecurityRule[]>([]);
  const [egressRules, setEgressRules] = useState<SecurityRule[]>([]);
  const [originalIngressRules, setOriginalIngressRules] = useState<SecurityRule[]>([]);
  const [originalEgressRules, setOriginalEgressRules] = useState<SecurityRule[]>([]);
  const [securityTab, setSecurityTab] = useState<'ingress' | 'egress'>('ingress');
  const [showAddRuleModal, setShowAddRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState<SecurityRule | null>(null);
  const [newRule, setNewRule] = useState<Omit<SecurityRule, 'id'>>(EMPTY_RULE);
  const [isSavingIngress, setIsSavingIngress] = useState(false);
  const [isSavingEgress, setIsSavingEgress] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Carga las reglas REALES del security group (incluye las que creó
  // CloudFormation al provisionar, p.ej. el puerto 80 abierto). Si el GET
  // falla, cae al objeto securityRules que venga en las props.
  useEffect(() => {
    let cancelled = false;
    const applyRules = (data: any) => {
      const ing = mapSecurityRulesFromAPI(data, 'ingress');
      const egr = mapSecurityRulesFromAPI(data, 'egress');
      setIngressRules(ing);
      setEgressRules(egr);
      setOriginalIngressRules(ing);
      setOriginalEgressRules(egr);
    };
    const load = async () => {
      if (!serviceId || !securityGroupId) {
        applyRules(securityRules);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const live = await servicesService.getLiveSecurityRules(serviceId);
        if (!cancelled) applyRules(live);
      } catch {
        if (!cancelled) applyRules(securityRules);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, securityGroupId]);

  const getUserIP = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch {
      return '';
    }
  };

  const handleTypeChange = (type: SecurityRule['type']) => {
    const config = getTypeConfig(type);
    setNewRule({ ...newRule, type, fromPort: config.fromPort, toPort: config.toPort, protocol: config.protocol });
  };

  const handleAddRule = () => {
    if (!newRule.cidr.trim() || !newRule.description.trim() || !newRule.fromPort || !newRule.toPort) {
      showError('Completa todos los campos requeridos.');
      return;
    }
    const rule: SecurityRule = { id: `rule-${Date.now()}-${Math.random()}`, ...newRule };
    if (newRule.direction === 'ingress') {
      setIngressRules([...ingressRules, rule]);
    } else {
      setEgressRules([...egressRules, rule]);
    }
    setNewRule(EMPTY_RULE);
    setShowAddRuleModal(false);
  };

  const handleDeleteRule = (ruleId: string, direction: 'ingress' | 'egress') => {
    if (direction === 'ingress') {
      setIngressRules(ingressRules.filter((r) => r.id !== ruleId));
    } else {
      setEgressRules(egressRules.filter((r) => r.id !== ruleId));
    }
  };

  const handleEditRule = (rule: SecurityRule) => {
    setEditingRule(rule);
    setNewRule({
      direction: rule.direction,
      type: rule.type,
      fromPort: rule.fromPort,
      toPort: rule.toPort,
      protocol: rule.protocol,
      cidr: rule.cidr,
      description: rule.description,
    });
    setShowAddRuleModal(true);
  };

  const handleUpdateRule = () => {
    if (!editingRule) return;
    if (!newRule.cidr.trim() || !newRule.description.trim() || !newRule.fromPort || !newRule.toPort) {
      showError('Completa todos los campos requeridos.');
      return;
    }
    // Mantiene id y dirección; actualiza el resto. Al guardar, el diff
    // revoca la regla anterior y autoriza la nueva en el SG.
    const updatedRule: SecurityRule = { ...editingRule, ...newRule, id: editingRule.id, direction: editingRule.direction };
    if (editingRule.direction === 'ingress') {
      setIngressRules(ingressRules.map((r) => (r.id === editingRule.id ? updatedRule : r)));
    } else {
      setEgressRules(egressRules.map((r) => (r.id === editingRule.id ? updatedRule : r)));
    }
    setEditingRule(null);
    setNewRule(EMPTY_RULE);
    setShowAddRuleModal(false);
  };

  const handleAddMyIP = async () => {
    const userIP = await getUserIP();
    if (userIP) {
      setNewRule({ ...newRule, cidr: `${userIP}/32` });
    } else {
      showError('No se pudo obtener tu IP pública.');
    }
  };

  const saveRules = async (direction: 'ingress' | 'egress') => {
    const rules = direction === 'ingress' ? ingressRules : egressRules;
    const originals = direction === 'ingress' ? originalIngressRules : originalEgressRules;
    const setSaving = direction === 'ingress' ? setIsSavingIngress : setIsSavingEgress;
    const setOriginals = direction === 'ingress' ? setOriginalIngressRules : setOriginalEgressRules;

    setSaving(true);
    try {
      const currentRules = rules.map((r) => ({
        protocol: r.protocol,
        fromPort: r.fromPort,
        toPort: r.toPort,
        cidr: r.cidr,
        description: r.description,
      }));
      const originalRules = originals.map((r) => ({
        protocol: r.protocol,
        fromPort: r.fromPort,
        toPort: r.toPort,
        cidr: r.cidr,
        description: r.description,
      }));

      const rulesToAdd = currentRules.filter(
        (cr) => !originalRules.some((or) => areRulesEqual(or, cr))
      );
      const rulesToRemove = originalRules.filter(
        (or) => !currentRules.some((cr) => areRulesEqual(or, cr))
      );

      if (rulesToAdd.length > 0 || rulesToRemove.length > 0) {
        await servicesService.updateSecurityRulesBatch(serviceId, direction, rulesToAdd, rulesToRemove);
      }
      await servicesService.saveSecurityRules(serviceId, direction, currentRules);
      setOriginals(rules);
      success('Reglas de seguridad actualizadas.');
    } catch (err: any) {
      showError(err?.message || 'Error al guardar las reglas de seguridad.');
    } finally {
      setSaving(false);
    }
  };

  if (!securityGroupId) {
    return (
      <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded-r-lg">
        <div className="flex items-start gap-2">
          <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 text-lg mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1">
              Reglas de seguridad no disponibles todavía
            </p>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              El servicio aún no tiene un grupo de seguridad asignado. Vuelve a intentarlo cuando el servicio
              esté en estado <span className="font-mono">running</span>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const renderRulesTable = (rules: SecurityRule[], direction: 'ingress' | 'egress') => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white">
          {direction === 'ingress' ? 'Reglas de Entrada' : 'Reglas de Salida'}
        </h4>
        <button
          onClick={() => {
            setNewRule({ ...EMPTY_RULE, direction });
            setEditingRule(null);
            setShowAddRuleModal(true);
          }}
          className="px-4 py-2 text-sm font-medium bg-black dark:bg-white text-white dark:text-black rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors flex items-center gap-2"
        >
          <PlusOutlined />
          Agregar regla
        </button>
      </div>
      {rules.length > 0 ? (
        <div className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Puerto</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Protocolo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">CIDR</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Descripción</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
              {rules.map((rule) => (
                <tr key={rule.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{rule.type}</td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">
                    {rule.fromPort === rule.toPort ? rule.fromPort : `${rule.fromPort}-${rule.toPort}`}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white uppercase">{rule.protocol}</td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">{rule.cidr}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{rule.description}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleEditRule(rule)}
                        className="p-1.5 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                        title="Editar"
                      >
                        <SettingOutlined />
                      </button>
                      <button
                        onClick={() => handleDeleteRule(rule.id, direction)}
                        className="p-1.5 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Eliminar"
                      >
                        <DeleteOutlined />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">No hay reglas configuradas.</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Agrega una regla para permitir tráfico hacia este servicio.
          </p>
        </div>
      )}
      <div className="flex items-center justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={() => saveRules(direction)}
          disabled={direction === 'ingress' ? isSavingIngress : isSavingEgress}
          className="px-6 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {(direction === 'ingress' ? isSavingIngress : isSavingEgress) ? 'Guardando...' : 'Guardar reglas'}
        </button>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="text-center py-10 text-gray-500 dark:text-gray-400">
        <LoadingOutlined className="text-2xl" spin />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Reglas de seguridad</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Controla qué tráfico de red puede entrar y salir de este servicio (security group).
        </p>
      </div>

      {/* Sub-tabs ingress / egress */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-1">
          <button
            onClick={() => setSecurityTab('ingress')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              securityTab === 'ingress'
                ? 'border-black dark:border-white text-gray-900 dark:text-white'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Reglas de Entrada (Ingress)
          </button>
          <button
            onClick={() => setSecurityTab('egress')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              securityTab === 'egress'
                ? 'border-black dark:border-white text-gray-900 dark:text-white'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Reglas de Salida (Egress)
          </button>
        </div>
      </div>

      {securityTab === 'ingress' ? renderRulesTable(ingressRules, 'ingress') : renderRulesTable(egressRules, 'egress')}

      {/* Modal agregar/editar regla */}
      {showAddRuleModal && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={() => {
              setShowAddRuleModal(false);
              setEditingRule(null);
              setNewRule(EMPTY_RULE);
            }}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="bg-white dark:bg-dark-bg rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white dark:bg-dark-bg border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-4 flex items-center justify-between gap-2">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                  {editingRule ? 'Editar regla' : 'Agregar regla'}
                </h3>
                <button
                  onClick={() => {
                    setShowAddRuleModal(false);
                    setEditingRule(null);
                    setNewRule(EMPTY_RULE);
                  }}
                  className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                >
                  <CloseCircleOutlined className="text-xl" />
                </button>
              </div>

              <div className="p-6 space-y-4 dark:bg-dark-bg">
                {/* Dirección */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Dirección *</label>
                  <select
                    value={newRule.direction}
                    onChange={(e) => setNewRule({ ...newRule, direction: e.target.value as 'ingress' | 'egress' })}
                    disabled={!!editingRule}
                    className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none font-mono ${
                      editingRule
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                    }`}
                  >
                    <option value="ingress">Ingress (Entrada)</option>
                    <option value="egress">Egress (Salida)</option>
                  </select>
                </div>

                {/* Tipo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipo</label>
                  <select
                    value={newRule.type}
                    onChange={(e) => handleTypeChange(e.target.value as SecurityRule['type'])}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="SSH">SSH</option>
                    <option value="RDP">RDP</option>
                    <option value="MSSQL">MSSQL</option>
                    <option value="MySQL">MySQL</option>
                    <option value="PostgreSQL">PostgreSQL</option>
                    <option value="HTTP">HTTP</option>
                    <option value="HTTPS">HTTPS</option>
                    <option value="Custom">Custom</option>
                  </select>
                </div>

                {/* Puertos */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Puerto Desde *</label>
                    <input
                      type="number"
                      value={newRule.fromPort}
                      onChange={(e) => setNewRule({ ...newRule, fromPort: parseInt(e.target.value) || 0 })}
                      min="1"
                      max="65535"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Puerto Hasta *</label>
                    <input
                      type="number"
                      value={newRule.toPort}
                      onChange={(e) => setNewRule({ ...newRule, toPort: parseInt(e.target.value) || 0 })}
                      min="1"
                      max="65535"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                {/* Protocolo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Protocolo</label>
                  <select
                    value={newRule.protocol}
                    onChange={(e) => setNewRule({ ...newRule, protocol: e.target.value as 'tcp' | 'udp' | 'icmp' })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="tcp">TCP</option>
                    <option value="udp">UDP</option>
                    <option value="icmp">ICMP</option>
                  </select>
                </div>

                {/* CIDR */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Origen (CIDR) *</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newRule.cidr}
                      onChange={(e) => setNewRule({ ...newRule, cidr: e.target.value })}
                      placeholder="0.0.0.0/0 o 192.168.1.0/24"
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={handleAddMyIP}
                      className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg transition-colors text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      Agregar mi IP
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Formato CIDR: 0.0.0.0/0 para permitir todo, o 192.168.1.0/24 para una red específica.
                  </p>
                </div>

                {/* Descripción */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Descripción *</label>
                  <input
                    type="text"
                    value={newRule.description}
                    onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
                    placeholder="Ej: Acceso desde oficina"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Al guardar, si cambiaste puerto/protocolo/origen se revoca la regla anterior y se crea la nueva en el firewall.
                  </p>
                </div>

                {/* Botones */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setShowAddRuleModal(false);
                      setEditingRule(null);
                      setNewRule(EMPTY_RULE);
                    }}
                    className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={editingRule ? handleUpdateRule : handleAddRule}
                    disabled={!newRule.cidr.trim() || !newRule.description.trim() || !newRule.fromPort || !newRule.toPort}
                    className="px-6 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editingRule ? 'Guardar' : 'Agregar regla'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SecurityRulesTab;
